import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Input, Select, Table } from 'antd';
import Chart from 'react-apexcharts';
import { T } from 'react-translator-component';

import { FilterField, Grid, KpiCard, Panel, Wrapper } from './styled';
import { useInternalTickets } from './useInternalTickets';

import { invalidateJiraCache } from '../../../hooks/jira';
import { InternalFilter } from '../../../types/responses/Jira';
import { Pagination } from '../../molecules/Pagination/Pagination';
import { escapeHtml } from '../../organisms/Tickets/escapeHtml';
import InternalTicketDrawer from '../../organisms/Tickets/InternalTicketDrawer';
import { priorityColor, statusColor } from '../Tickets/statusLabels';

// Os tipos do antd 4.10 com @types/react 18 nao declaram `children` em Select.Option; usamos `options`.
const bar = (title: string, data: { label: string; count: number }[], latest = false) => {
  // byType/byClient vem ordenado por contagem (top 8); a tendencia e cronologica: ultimos 8 meses.
  const shown = latest ? data.slice(-8) : data.slice(0, 8);
  // O ApexCharts 3.23 escreve rotulos e nomes de serie com innerHTML no tooltip: tudo que vem
  // de dados (nome de empresa, tipo) entra escapado, e os formatters do tooltip escapam a partir
  // do texto cru (uma vez so), para nada chegar ao DOM sem escape.
  const raw = shown.map((d) => d.label);
  const seriesName = T('Tickets');
  const options = {
    chart: { toolbar: { show: false } },
    xaxis: { categories: raw.map(escapeHtml), tooltip: { enabled: false } },
    tooltip: {
      x: { formatter: (_v: unknown, o?: { dataPointIndex?: number }) => escapeHtml(raw[o?.dataPointIndex ?? -1] ?? '') },
      y: {
        formatter: (v: unknown) => escapeHtml(v),
        title: { formatter: () => escapeHtml(seriesName) },
      },
    },
  };
  return (
  <Panel>
    <h3>{T(title)}</h3>
    {shown.length === 0 ? (
      <span>{T('No data')}</span>
    ) : (
      <Chart
        type="bar"
        height={220}
        options={options}
        series={[{ name: escapeHtml(seriesName), data: shown.map((d) => d.count) }]}
      />
    )}
  </Panel>
  );
};

const TicketsInternalPage = () => {
  const s = useInternalTickets();
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshingTypes, setRefreshingTypes] = useState(false);
  const [typesError, setTypesError] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  if (s.forbidden)
    return (
      <Wrapper>
        <Alert type="warning" message={T('You do not have access to this area')} />
      </Wrapper>
    );

  const set = (patch: Partial<InternalFilter>) => s.setFilter({ ...s.filter, ...patch });

  const refreshTypes = async () => {
    if (refreshingTypes) return;
    setRefreshingTypes(true);
    setTypesError(false);
    try {
      await invalidateJiraCache();
      if (!mounted.current) return;
      await s.refresh();
    } catch {
      if (mounted.current) setTypesError(true);
    } finally {
      if (mounted.current) setRefreshingTypes(false);
    }
  };

  const kpi = (value: number | undefined) => (value === undefined ? '—' : value);

  return (
    <Wrapper>
      <h1>{T('Ticket management — Move internal')}</h1>
      {s.error && (
        <Alert
          type="error"
          message={T('Could not load tickets')}
          action={
            <Button size="small" onClick={s.refresh}>
              {T('Try again')}
            </Button>
          }
        />
      )}
      {typesError && <Alert type="error" message={T('Could not refresh the types')} />}

      <Grid cols={5}>
        <KpiCard accent="#8c8c8c">
          <strong>{kpi(s.kpis?.total)}</strong>
          <span>{T('Total')}</span>
        </KpiCard>
        <KpiCard accent={statusColor.open}>
          <strong>{kpi(s.kpis?.open)}</strong>
          <span>{T('Open tickets')}</span>
        </KpiCard>
        <KpiCard accent={statusColor.inProgress}>
          <strong>{kpi(s.kpis?.inProgress)}</strong>
          <span>{T('In progress')}</span>
        </KpiCard>
        <KpiCard accent="#f5222d">
          <strong>{kpi(s.kpis?.slaAtRisk)}</strong>
          <span>{T('SLA at risk')}</span>
        </KpiCard>
        <KpiCard accent={statusColor.done}>
          <strong>{kpi(s.kpis?.resolvedToday)}</strong>
          <span>{T('Resolved today')}</span>
        </KpiCard>
      </Grid>
      <small>{T('Totals are global and not affected by the filters')}</small>

      <Grid cols={3}>
        {bar('Most frequent types', s.byType)}
        {bar('Tickets by company', s.byClient)}
        {bar('Monthly volume', s.trend, true)}
      </Grid>
      <small>{T('Based on the loaded list')}</small>

      <Panel>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
          <FilterField>
            <label htmlFor="jira-filter-client">{T('Company')}</label>
            <Input
              id="jira-filter-client"
              allowClear
              style={{ width: 180 }}
              value={s.filter.client ?? ''}
              onChange={(e) => set({ client: e.target.value })}
            />
          </FilterField>
          <FilterField>
            <label htmlFor="jira-filter-status">{T('Status')}</label>
            <Select
              id="jira-filter-status"
              allowClear
              style={{ width: 160 }}
              value={s.filter.status}
              onChange={(v) => set({ status: v as string | undefined })}
              options={[
                { value: 'open', label: T('Open tickets') },
                { value: 'inProgress', label: T('In progress') },
                { value: 'done', label: T('Resolved tickets') },
              ]}
            />
          </FilterField>
          <FilterField>
            <label htmlFor="jira-filter-priority">{T('Priority')}</label>
            <Select
              id="jira-filter-priority"
              allowClear
              style={{ width: 160 }}
              value={s.filter.priority}
              onChange={(v) => set({ priority: v as string | undefined })}
              options={[
                { value: 'critical', label: T('Critical priority') },
                { value: 'high', label: T('High priority') },
                { value: 'medium', label: T('Medium priority') },
                { value: 'low', label: T('Low priority') },
              ]}
            />
          </FilterField>
          <FilterField>
            <label htmlFor="jira-filter-type">{T('Type')}</label>
            <Input
              id="jira-filter-type"
              allowClear
              style={{ width: 160 }}
              value={s.filter.type ?? ''}
              onChange={(e) => set({ type: e.target.value })}
            />
          </FilterField>
          <FilterField>
            <label htmlFor="jira-filter-assignee">{T('Assignee')}</label>
            <Input
              id="jira-filter-assignee"
              allowClear
              style={{ width: 160 }}
              value={s.filter.assignee ?? ''}
              onChange={(e) => set({ assignee: e.target.value })}
            />
          </FilterField>
          <Button loading={refreshingTypes} onClick={refreshTypes}>
            {T('Refresh types')}
          </Button>
        </div>
        <Table
          rowKey="key"
          loading={s.loading}
          dataSource={s.items}
          pagination={false}
          onRow={(r) => ({ onClick: () => setSelected(r.key) })}
          columns={[
            {
              title: T('Ticket'),
              dataIndex: 'key',
              render: (k: string) => (
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(k);
                  }}
                >
                  {k}
                </Button>
              ),
            },
            { title: T('Title'), dataIndex: 'title' },
            { title: T('Company'), dataIndex: 'organizationName' },
            { title: T('Type'), dataIndex: 'requestType' },
            {
              title: T('Priority'),
              dataIndex: 'priorityCategory',
              render: (p: string) => <span style={{ color: priorityColor[p] }}>{p}</span>,
            },
            { title: T('Status'), dataIndex: 'statusLabel' },
          ]}
        />
        <Pagination
          currentPage={s.page}
          pageSize={s.pageSize}
          totalCount={s.totalCount}
          onPageChange={(page: number) => s.setPage(page)}
        />
      </Panel>

      <InternalTicketDrawer ticketKey={selected} onClose={() => setSelected(null)} onChanged={s.refresh} />
    </Wrapper>
  );
};

export default TicketsInternalPage;
